/**
 * StepathonPage — public landing for the Step-a-Thon hub at `/stepathon`.
 * Shows the umbrella Million Mile Movement counter + grid of all events
 * (active / upcoming / past) with links to each event detail page.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import LiveStepCounter from "../components/LiveStepCounter";
import { Button } from "../components/ui/button";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function EventCard({ ev }) {
  const pct = ev.percent_to_goal;
  const isLive = ev.status === "active";
  return (
    <Link
      to={`/stepathon/${ev.slug}`}
      data-testid={`event-card-${ev.slug}`}
      className="group rounded-3xl border border-purple-200/40 bg-white/5
                 backdrop-blur p-6 hover:border-pink-300/60 transition"
    >
      <div className="flex items-center justify-between">
        <span className={`text-[11px] tracking-widest uppercase font-bold ${
          isLive ? "text-pink-300" : "text-purple-200/70"
        }`}>
          {ev.status}
        </span>
        <span className="text-xs text-purple-200/70">
          Goal {(ev.goal_steps / 1_000_000).toLocaleString()}M steps
        </span>
      </div>
      <h3 className="mt-3 text-2xl font-extrabold text-white group-hover:text-pink-200 transition">
        {ev.name}
      </h3>
      <p className="mt-2 text-sm text-purple-100/80 line-clamp-3">{ev.description}</p>
      <div className="mt-5 h-2 rounded-full bg-purple-900/40 overflow-hidden">
        <div className="h-full bg-gradient-to-r from-purple-400 to-pink-400"
             style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
      <div className="mt-3 flex items-center justify-between text-xs text-purple-100/80">
        <span><b className="text-white tabular-nums">{ev.total_steps.toLocaleString()}</b> steps</span>
        <span>{pct.toFixed(2)}% of goal</span>
        <span>{ev.participant_count.toLocaleString()} walkers</span>
      </div>
    </Link>
  );
}

export default function StepathonPage() {
  const [events, setEvents] = useState([]);
  useEffect(() => {
    axios.get(`${BACKEND}/api/stepathon/events`)
      .then((r) => setEvents(r.data.events || []))
      .catch(() => setEvents([]));
  }, []);
  const active = events.filter(e => e.status === "active");
  const upcoming = events.filter(e => e.status === "upcoming");
  const ended = events.filter(e => e.status === "ended");

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1B0B3A] via-[#150728] to-[#0a0612] text-white">
      <div className="max-w-6xl mx-auto px-6 py-16">
        {/* Hero */}
        <div className="text-center max-w-3xl mx-auto">
          <p className="text-xs tracking-[0.5em] uppercase text-pink-300 font-bold">
            GLPFlo · Step-a-Thon
          </p>
          <h1 className="mt-4 text-5xl sm:text-6xl font-black leading-tight">
            Normalize the journey,<br/>
            <span className="bg-gradient-to-r from-purple-300 to-pink-300 bg-clip-text text-transparent">
              one step at a time.
            </span>
          </h1>
          <p className="mt-6 text-lg text-purple-100/85">
            Multiple community challenges. One umbrella movement. Every step
            you log on any GLPFlo step-a-thon rolls up into the global
            Million Mile Movement counter below.
          </p>
        </div>
        {/* Global counter */}
        <div className="mt-14 rounded-3xl border border-purple-300/30 bg-purple-900/30 p-10 backdrop-blur">
          <LiveStepCounter
            url="/api/stepathon/global/counter"
            label="Million Mile Movement"
            sublabel="lifetime steps logged across every GLPFlo step-a-thon"
            variant="hero"
          />
        </div>

        {/* Active events */}
        {active.length > 0 && (
          <section className="mt-16">
            <h2 className="text-2xl font-extrabold mb-6">🟢 Active now</h2>
            <div className="grid sm:grid-cols-2 gap-5">
              {active.map(e => <EventCard key={e.slug} ev={e} />)}
            </div>
          </section>
        )}
        {upcoming.length > 0 && (
          <section className="mt-12">
            <h2 className="text-2xl font-extrabold mb-6">🔜 Upcoming</h2>
            <div className="grid sm:grid-cols-2 gap-5">
              {upcoming.map(e => <EventCard key={e.slug} ev={e} />)}
            </div>
          </section>
        )}
        {ended.length > 0 && (
          <section className="mt-12">
            <h2 className="text-2xl font-extrabold mb-6">🏁 Past events</h2>
            <div className="grid sm:grid-cols-2 gap-5 opacity-80">
              {ended.map(e => <EventCard key={e.slug} ev={e} />)}
            </div>
          </section>
        )}

        {active.length === 0 && upcoming.length === 0 && ended.length === 0 && (
          <section className="mt-16 rounded-3xl border border-purple-300/30 bg-purple-900/20 p-10 text-center backdrop-blur">
            <p className="text-2xl font-extrabold mb-2">🚶 New events launching soon</p>
            <p className="text-purple-100/85 max-w-xl mx-auto">
              We&apos;re spinning up new community walks and marathons. In the meantime, every step you log inside the GLPFlo app still counts toward the Million Mile Movement counter above.
            </p>
            <Link to="/login" data-testid="stepathon-empty-cta">
              <Button className="mt-6 bg-pink-600 hover:bg-pink-700 text-white text-base px-8 py-6 rounded-full">
                Log in &amp; start walking
              </Button>
            </Link>
          </section>
        )}

        <div className="mt-16 text-center">
          <Link to="/login">
            <Button data-testid="stepathon-join-cta"
              className="bg-pink-600 hover:bg-pink-700 text-white text-base px-8 py-6 rounded-full">
              Log in &amp; start walking
            </Button>
          </Link>
          <p className="mt-3 text-xs text-purple-200/80">
            Free for every GLPFlo member · no purchase required to participate
          </p>
        </div>
      </div>
    </div>
  );
}
